import { useState, useEffect, useCallback, useRef } from 'react'
import io from 'socket.io-client'

// 동적으로 소켓 서버 URL 결정
const getSocketServerURL = () => {
  const hostname = window.location.hostname
  const protocol = window.location.protocol === 'https:' ? 'https' : 'https' // HTTPS 우선 사용
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}://localhost:3001`
  } else {
    // 다른 기기에서 접근하는 경우 현재 호스트의 IP 사용
    return `${protocol}://${hostname}:3001`
  }
}
const SOCKET_SERVER_URL = getSocketServerURL()

export const useWebRTC = (
  userType = 'user',
  externalSocket = null,
  onCallEnded = null,
  initialLocalStream = null,
) => {
  const [socket, setSocket] = useState(null)
  const [localStream, setLocalStream] = useState(null)
  const [remoteStream, setRemoteStream] = useState(null)
  const [callState, setCallState] = useState('idle') // idle, waiting_queue, calling, active
  const [isIncomingCall, setIsIncomingCall] = useState(false)
  const [queuePosition, setQueuePosition] = useState(null) // 대기열 위치
  const peerConnectionRef = useRef(null)
  const pendingIceCandidates = useRef([])
  const isInitiator = useRef(false)
  const endCallInitiated = useRef(false) // 통화 종료 중복 방지 플래그
  const pendingOfferRef = useRef(null) // 대기 중인 offer 저장
  const currentCallId = useRef(null) // 현재 통화 ID

  // 소켓 연결 초기화 (외부 소켓이 없을 때만)
  useEffect(() => {
    if (externalSocket) {
      console.log('외부 소켓 사용:', externalSocket.id, 'userType:', userType)
      setSocket(externalSocket)
      return
    }

    console.log('자체 소켓 연결 시도...', 'userType:', userType)
    const newSocket = io(SOCKET_SERVER_URL)
    newSocket.on('connect', () => {
      console.log('자체 소켓 연결 성공:', newSocket.id, 'userType:', userType)
      // 연결 성공 시 서버에 사용자 타입 전송
      newSocket.emit('user-type', userType)
    })

    newSocket.on('connect_error', (error) => {
      console.error('소켓 연결 실패:', error)
    })

    setSocket(newSocket)

    return () => {
      if (!externalSocket) {
        console.log('자체 소켓 연결 해제')
        newSocket.close()
      }
    }
  }, [userType, externalSocket])

  // 미디어 스트림 초기화
  useEffect(() => {
    // 초기 스트림이 전달된 경우 사용
    if (initialLocalStream) {
      console.log('초기 미디어 스트림 사용:', initialLocalStream)
      setLocalStream(initialLocalStream)
      return
    }

    const initializeMedia = async () => {
      console.log('미디어 스트림 초기화 시도...')
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        })
        console.log('미디어 스트림 초기화 성공')
        setLocalStream(stream)
      } catch (error) {
        console.error('미디어 스트림 초기화 실패:', error)
      }
    }

    initializeMedia()
  }, [initialLocalStream])

  // 피어 연결 생성
  const createPeerConnection = useCallback(() => {
    console.log('새 피어 연결 생성')
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    }

    const pc = new RTCPeerConnection(configuration)

    // ICE candidate 이벤트
    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        console.log('ICE candidate 생성:', event.candidate.type)
        socket.emit('ice-candidate', event.candidate)
      }
    }

    // 원격 스트림 수신
    pc.ontrack = (event) => {
      console.log('원격 스트림 수신됨:', event.streams[0].getTracks().length, '개 트랙')
      setRemoteStream(event.streams[0])
    }

    // 연결 상태 모니터링
    pc.onconnectionstatechange = () => {
      console.log('연결 상태:', pc.connectionState)
      if (pc.connectionState === 'connected') {
        setCallState('active')
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        // 연결 실패/끊김 시 상태만 변경, 중복 종료 방지
        if (!endCallInitiated.current) {
          setCallState('idle')
          setRemoteStream(null)
        }
      }
    }

    pc.oniceconnectionstatechange = () => {
      console.log('ICE 연결 상태:', pc.iceConnectionState)
    }

    pc.onsignalingstatechange = () => {
      console.log('시그널링 상태:', pc.signalingState)
    }

    // 로컬 스트림 추가
    if (localStream) {
      console.log('로컬 스트림 트랙 추가:', localStream.getTracks().length, '개')
      localStream.getTracks().forEach((track) => {
        console.log('트랙 추가:', track.kind)
        pc.addTrack(track, localStream)
      })
    }

    return pc
  }, [socket, localStream])

  // 통화 시작 - 대기열 시스템 사용
  const startCall = useCallback(() => {
    if (!socket || !localStream) {
      console.warn('소켓 또는 로컬 스트림이 없습니다')
      return
    }

    console.log('통화 요청 - 대기열에 추가 요청')
    setCallState('waiting_queue')
    // 서버 대기열에 통화 요청
    socket.emit('request-call')
  }, [socket, localStream])

  // WebRTC Offer 생성 (대기열에서 수락된 후 호출)
  const createOffer = useCallback(async () => {
    if (!localStream) {
      console.error('로컬 스트림이 없습니다')
      return
    }

    console.log('통화 수락됨 - Offer 생성')
    isInitiator.current = true
    setCallState('calling')

    // 기존 연결 정리
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close()
    }

    const pc = createPeerConnection()
    peerConnectionRef.current = pc

    try {
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      })
      await pc.setLocalDescription(offer)
      console.log('Offer 전송')
      socket.emit('offer', offer)
    } catch (error) {
      console.error('Offer 생성 실패:', error)
      setCallState('idle')
    }
  }, [socket, localStream, createPeerConnection])

  // 통화 종료
  const endCall = useCallback(() => {
    // 이미 통화 종료가 진행 중이면 중복 처리 방지
    if (endCallInitiated.current) {
      console.log('통화 종료 이미 진행 중')
      return
    }
    console.log('통화 종료')
    endCallInitiated.current = true
    if (socket) {
      socket.emit('end-call')
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }
    setCallState('idle')
    setRemoteStream(null)
    setIsIncomingCall(false)
    setQueuePosition(null)
    pendingIceCandidates.current = []
    isInitiator.current = false
    pendingOfferRef.current = null
    currentCallId.current = null // currentCallId 초기화 추가
    // 외부 콜백 호출 (관리자가 대기열로 돌아가기 위함)
    if (onCallEnded) {
      onCallEnded()
    }
    // 플래그 리셋 (다음 통화를 위해)
    setTimeout(() => {
      endCallInitiated.current = false
    }, 1000)
  }, [socket, onCallEnded])

  // 수신 통화 수락
  const acceptCall = useCallback(async () => {
    console.log('통화 수락 - Answer 생성 시작')
    // 직접 통화인 경우 서버에 수락 신호 먼저 전송
    if (currentCallId.current) {
      socket.emit('accept-direct-call', { callId: currentCallId.current })
    }
    if (!pendingOfferRef.current || !peerConnectionRef.current) {
      console.error('대기 중인 offer 또는 peer connection이 없습니다')
      return
    }

    try {
      const answer = await peerConnectionRef.current.createAnswer()
      await peerConnectionRef.current.setLocalDescription(answer)
      console.log('Answer 생성 및 전송')
      socket.emit('answer', answer)
      setIsIncomingCall(false)
      setCallState('calling')
      // pending ICE candidates 처리
      pendingIceCandidates.current.forEach((candidate) => {
        peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate))
      })
      pendingIceCandidates.current = []
      pendingOfferRef.current = null
    } catch (error) {
      console.error('통화 수락 실패:', error)
      setCallState('idle')
      setIsIncomingCall(false)
    }
  }, [socket])

  // 수신 통화 거절
  const rejectCall = useCallback(() => {
    console.log('통화 거절 시작 - 현재 상태:', callState, 'callId:', currentCallId.current)
    // 직접 통화인 경우 (callId가 있는 경우)
    if (currentCallId.current) {
      console.log('직접 통화 거절:', currentCallId.current)
      socket.emit('reject-direct-call', { callId: currentCallId.current })
    } else {
      // 일반 WebRTC offer/answer 단계에서의 거절
      console.log('WebRTC 통화 거절 - call-rejected 이벤트 전송')
      socket.emit('call-rejected')
    }
    // 상태 초기화
    setIsIncomingCall(false)
    setCallState('idle')
    setQueuePosition(null)
    pendingOfferRef.current = null
    currentCallId.current = null
    // PeerConnection 정리
    if (peerConnectionRef.current) {
      console.log('PeerConnection 정리')
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }
    pendingIceCandidates.current = []
    // 외부 콜백 호출 (관리자 화면 복원)
    if (onCallEnded) {
      onCallEnded()
    }
  }, [socket, callState, onCallEnded])

  // 통화 요청 취소
  const cancelCall = useCallback(() => {
    console.log('통화 요청 취소 - 현재 상태:', callState, 'callId:', currentCallId.current)
    if (socket) {
      // 현재 상태에 따라 다른 취소 신호 전송
      if (currentCallId.current) {
        console.log('활성 통화 취소 - call-rejected 이벤트 전송')
        socket.emit('call-rejected')
      } else {
        console.log('대기열 요청 취소 - cancel-call-request 이벤트 전송')
        socket.emit('cancel-call-request')
      }
    }
    // 상태 완전 초기화
    setCallState('idle')
    setQueuePosition(null)
    setIsIncomingCall(false)
    // 연결 정리
    if (peerConnectionRef.current) {
      console.log('취소로 인한 PeerConnection 정리')
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }
    // 모든 변수 초기화
    pendingIceCandidates.current = []
    pendingOfferRef.current = null
    isInitiator.current = false
    currentCallId.current = null
    // 관리자 화면 복원
    if (onCallEnded) {
      console.log('취소로 인한 관리자 화면 복원')
      onCallEnded()
    }
  }, [socket, callState, onCallEnded])

  // 관리자용 통화 수락 함수 (대기열에서 특정 요청 수락)
  const acceptQueuedCall = useCallback(
    (requestId) => {
      if (!socket) {
        console.warn('소켓이 연결되지 않았습니다')
        return
      }

      console.log('대기열 통화 수락:', requestId)
      socket.emit('accept-call', { requestId })
    },
    [socket],
  )

  // 소켓 이벤트 리스너
  useEffect(() => {
    if (!socket) return

    // 대기열 관련 이벤트 핸들러들
    const handleCallRequestSent = (data) => {
      console.log('통화 요청이 대기열에 추가됨:', data.requestId)
      setCallState('waiting_queue')
    }

    const handleCallAccepted = (data) => {
      console.log('통화 수락됨:', data)
      // 상태 초기화 후 새로운 callId 설정
      endCallInitiated.current = false
      currentCallId.current = data.callId
      setCallState('calling')
      // 통화가 수락되면 Offer 생성
      createOffer()
    }

    const handleCallAcceptedAsAdmin = (data) => {
      console.log('관리자로서 통화 수락됨:', data)
      // 상태 초기화 후 새로운 callId 설정
      endCallInitiated.current = false
      currentCallId.current = data.callId
      setCallState('calling')
      // 관리자는 사용자의 Offer를 기다림
    }

    const handleCallQueueUpdate = (data) => {
      console.log('대기열 업데이트:', data)
      // 현재 사용자의 대기열 위치 찾기
      if (socket.id) {
        const myRequest = data.queue.find((req) => req.userId === socket.id)
        if (myRequest) {
          const position = data.queue.findIndex((req) => req.userId === socket.id) + 1
          setQueuePosition(position)
          console.log('대기열 위치:', position)
        } else {
          setQueuePosition(null)
        }
      }
    }

    const handleCallRequestCancelled = () => {
      console.log('통화 요청 취소 확인됨')
      setCallState('idle')
      setQueuePosition(null)
      setIsIncomingCall(false)
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close()
        peerConnectionRef.current = null
      }
      pendingIceCandidates.current = []
      pendingOfferRef.current = null
      isInitiator.current = false
      currentCallId.current = null
    }

    // 직접 통화 수신 이벤트 핸들러
    const handleDirectCallIncoming = (data) => {
      console.log('직접 통화 수신:', data)
      setIsIncomingCall(true)
      setCallState('calling')
      currentCallId.current = data.callId
    }

    // 기존 WebRTC 이벤트 핸들러들
    const handleOffer = async (offer) => {
      console.log('Offer 수신 - 수락/거절 대기 상태로 전환')
      try {
        // 기존 연결 정리
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close()
        }

        // 새로운 WebRTC 연결로 인한 상태 초기화
        currentCallId.current = null
        endCallInitiated.current = false

        const pc = createPeerConnection()
        peerConnectionRef.current = pc
        await pc.setRemoteDescription(new RTCSessionDescription(offer))
        console.log('Offer 설정 완료 - 사용자 응답 대기')

        // offer를 저장하고 사용자의 수락/거절을 기다림
        pendingOfferRef.current = offer
        setIsIncomingCall(true)
        setCallState('calling')
      } catch (error) {
        console.error('Offer 처리 실패:', error)
        setCallState('idle')
        setIsIncomingCall(false)
      }
    }

    const handleAnswer = async (answer) => {
      console.log('Answer 수신')
      if (
        peerConnectionRef.current &&
        peerConnectionRef.current.signalingState === 'have-local-offer'
      ) {
        try {
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer))
          console.log('Answer 설정 완료')

          // pending ICE candidates 처리
          pendingIceCandidates.current.forEach((candidate) => {
            peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate))
          })
          pendingIceCandidates.current = []
        } catch (error) {
          console.error('Answer 설정 실패:', error)
        }
      }
    }

    const handleIceCandidate = async (candidate) => {
      console.log('ICE candidate 수신')
      if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate))
        } catch (error) {
          console.error('ICE candidate 추가 실패:', error)
        }
      } else {
        pendingIceCandidates.current.push(candidate)
      }
    }

    const handleEndCall = () => {
      console.log('통화 종료 요청 수신')
      // 자신이 통화 종료를 시작한 경우 중복 처리 방지
      if (endCallInitiated.current) {
        console.log('통화 종료 중복 처리 방지')
        return
      }
      endCall()
    }

    const handleCallRejected = () => {
      console.log('상대방이 통화를 거절했습니다')
      // 상태 완전 초기화
      setCallState('idle')
      setIsIncomingCall(false)
      setQueuePosition(null)
      // 연결 정리
      if (peerConnectionRef.current) {
        console.log('통화 거절로 인한 PeerConnection 정리')
        peerConnectionRef.current.close()
        peerConnectionRef.current = null
      }
      // 모든 변수 초기화
      pendingIceCandidates.current = []
      pendingOfferRef.current = null
      isInitiator.current = false
      currentCallId.current = null
      // 관리자 화면 복원
      if (onCallEnded) {
        console.log('거절로 인한 관리자 화면 복원')
        onCallEnded()
      }
    }

    // 대기열 이벤트 리스너 등록
    socket.on('call-request-sent', handleCallRequestSent)
    socket.on('call-accepted', handleCallAccepted)
    socket.on('call-accepted-as-admin', handleCallAcceptedAsAdmin)
    socket.on('call-queue-update', handleCallQueueUpdate)
    socket.on('call-request-cancelled', handleCallRequestCancelled)
    // 직접 통화 이벤트 리스너 등록
    socket.on('direct-call-incoming', handleDirectCallIncoming)
    // 기존 WebRTC 이벤트 리스너 등록
    socket.on('offer', handleOffer)
    socket.on('answer', handleAnswer)
    socket.on('ice-candidate', handleIceCandidate)
    socket.on('call-rejected', handleCallRejected)
    socket.on('end-call', handleEndCall)

    return () => {
      // 대기열 이벤트 리스너 해제
      socket.off('call-request-sent', handleCallRequestSent)
      socket.off('call-accepted', handleCallAccepted)
      socket.off('call-accepted-as-admin', handleCallAcceptedAsAdmin)
      socket.off('call-queue-update', handleCallQueueUpdate)
      socket.off('call-request-cancelled', handleCallRequestCancelled)
      // 직접 통화 이벤트 리스너 해제
      socket.off('direct-call-incoming', handleDirectCallIncoming)
      // 기존 WebRTC 이벤트 리스너 해제
      socket.off('offer', handleOffer)
      socket.off('answer', handleAnswer)
      socket.off('ice-candidate', handleIceCandidate)
      socket.off('call-rejected', handleCallRejected)
      socket.off('end-call', handleEndCall)
    }
  }, [socket, createPeerConnection, endCall, createOffer])

  return {
    localStream,
    remoteStream,
    callState,
    isIncomingCall,
    queuePosition, // 대기열 위치 추가
    startCall,
    endCall,
    acceptCall,
    rejectCall,
    cancelCall,
    acceptQueuedCall, // 관리자용 대기열 통화 수락 함수 추가
  }
}
